// Redux slice and async thunks for session and authentication state
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

import {
  rolelist,
  type AuthTokenData,
  type CommonServicePayload,
  type SessionState,
  type User,
  type UserValidation,
} from '../types/session.types';
import sessionAPI from '../api/sessionAPI';
import appLocalStorage from './appLocalStorage';
import { Roles, RolesEnv } from '@/app/enums';
import { isTokenExpired } from '../utils/helpers';
import { getAssetHierarchy } from '@/features/assetHierarchy/store/assetHierarchy.thunk';

// Initial state for session slice
const initialState: SessionState = {
  user: null,
  loading: false,
  appInitialised: false,
  role: Roles.UNKNOWN,
  userAccessProfile: null,
  userRejected: false,
  responseErrorStatus: 0,
};

// Async thunk for sign-in flow
export const signIn = createAsyncThunk(
  'session/signIn',
  (_, { rejectWithValue }) => {
    try {
      return sessionAPI.signIn();
    } catch (error: any) {
      const { message } = error as Error;
      return rejectWithValue(message);
    }
  }
);

// Async thunk for beginning a session (token exchange and user info)
export const beginSession = createAsyncThunk<
  User | null,
  string | null,
  { rejectValue: string }
>('session/beginSession', async (code: string | null, { rejectWithValue }) => {
  try {
    const tokenData = appLocalStorage.getTokenData();
    if (tokenData) {
      let storedIdToken: any = tokenData?.id_token
        ? jwtDecode(tokenData?.id_token)
        : null;
      if (isTokenExpired(storedIdToken)) {
        // Guard: if missing refresh token (fresh session), skip refresh
        if (!tokenData?.refresh_token) {
          return rejectWithValue(
            'Session expired and no refresh token available.'
          );
        }
        try {
          const tokenResponse = await sessionAPI.refreshAccessToken(
            tokenData.refresh_token
          );
          if (!tokenResponse || !tokenResponse.data) {
            return rejectWithValue(
              'Invalid token refresh response: missing data'
            );
          }
          if (!tokenResponse.data.id_token) {
            return rejectWithValue(
              'Invalid token refresh response: missing id_token'
            );
          }
          const newTokenData = {
            ...tokenResponse.data,
            refresh_token: tokenData.refresh_token,
          };
          storedIdToken = jwtDecode(newTokenData.id_token);
          appLocalStorage.setTokenData(newTokenData);
        } catch (err) {
          // On refresh failure, clear and propagate error
          appLocalStorage.clearLocalStorage();
          const { message } = (err as Error) || {
            message: 'Token refresh failed',
          };
          return rejectWithValue(message);
        }
      }

      return {
        email: String(storedIdToken.email ?? ''),
        givenName: String(storedIdToken.given_name ?? ''),
        familyName: String(storedIdToken.family_name ?? ''),
      };
    }
    if (!code) {
      return null;
    }

    const tokenDataResponse: AxiosResponse<AuthTokenData> =
      await sessionAPI.getAuthToken(code);

    appLocalStorage.setTokenData(tokenDataResponse.data);
    const idToken = (
      tokenDataResponse.data.id_token
        ? (jwtDecode(tokenDataResponse.data.id_token) as Record<
            string,
            unknown
          >)
        : null
    ) as Record<string, unknown> | null;

    return {
      email: String((idToken?.email as string) ?? ''),
      givenName: String((idToken?.given_name as string) ?? ''),
      familyName: String((idToken?.family_name as string) ?? ''),
    };
  } catch (error: any) {
    const { message } = error as Error;
    return rejectWithValue(message);
  }
});

// Async thunk for loading user profile and access validation
export const loadUserProfile = createAsyncThunk<
  UserValidation | null,
  CommonServicePayload,
  { rejectValue: string }
>(
  'tags/loadUserProfile',
  async (commonServicePayload: CommonServicePayload, { rejectWithValue }) => {
    try {
      const userAccessProfile = appLocalStorage.getUserAccessProfile();

      if (userAccessProfile) {
        return userAccessProfile;
      }
      if (commonServicePayload?.token) {
        const response = await sessionAPI.getUserProfile(commonServicePayload);

        appLocalStorage.setAccessUserProfile(response.data);
        return response.data;
      }
      return null;
    } catch (err: any) {
      const { message } = err as Error;
      return rejectWithValue(message);
    }
  }
);

// Session slice with reducers for authentication, user, and loading state
export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signOut: state => {
      state.user = null;
      state.loading = false;
      state.appInitialised = true;
      appLocalStorage.clearLocalStorage();
    },
    // Marks app as authenticated
    setIsAuthenticated: state => {
      state.appInitialised = true;
    },
    // Clears user data and local storage
    clearUserData: state => {
      state.user = null;
      appLocalStorage.clearLocalStorage();
    },
    // Sets user info in state
    setUser: (state, action) => {
      state.user = action.payload;
      state.loading = false;
    },
    // Sets user profile and determines role
    setUserProfile: (state, action) => {
      state.userAccessProfile = action.payload;

      if (action.payload) {
        const {
          roles: { access },
        } = action.payload;

        const currentRoleEnv: RolesEnv =
          import.meta.env.VITE_APP_ENV === RolesEnv.PROD
            ? RolesEnv.PROD
            : RolesEnv.TEST;

        const currentAccessRoles = access.find(
          accessRoles => accessRoles.env === currentRoleEnv
        )?.roles;

        const role =
          currentAccessRoles?.length === 1
            ? currentAccessRoles[0].role
            : currentAccessRoles?.sort(
                (a, b) =>
                  (rolelist.find(e => e.role === a.role)?.priority || 0) -
                  (rolelist.find(e => e.role === b.role)?.priority || 1)
              )[0]?.role;
        state.userRejected = role ? false : true;
        state.role = (role as Roles) || Roles.UNKNOWN;
        state.userAccessProfile = action.payload;
      }
      state.appInitialised = true;
      state.loading = false;
    },
    // Sets app initialised state
    setAppInitialised: (state, action) => {
      state.appInitialised = action.payload;
    },
    // Sets loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setResponseErrorStatus: (state, action) => {
      state.responseErrorStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Handles beginSession async thunk states
      .addCase(getAssetHierarchy.rejected, (state, action) => {
        const payload = action.payload as { status?: number } | undefined;
        state.responseErrorStatus = payload?.status ?? 0;
      })
      .addCase(beginSession.pending, state => {
        state.loading = true;
      })
      .addCase(beginSession.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(beginSession.rejected, state => {
        state.loading = false;
        state.user = null;
      })
      // Handles loadUserProfile async thunk states
      .addCase(loadUserProfile.pending, state => {
        state.loading = true;
        state.userRejected = false;
      })
      .addCase(loadUserProfile.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload) {
          const {
            roles: { access },
          } = action.payload;

          const currentRoleEnv: RolesEnv =
            import.meta.env.VITE_APP_ENV === RolesEnv.PROD
              ? RolesEnv.PROD
              : RolesEnv.TEST;

          const currentAccessRoles = access.find(
            accessRoles => accessRoles.env === currentRoleEnv
          )?.roles;

          const role =
            currentAccessRoles?.length === 1
              ? currentAccessRoles[0].role
              : currentAccessRoles?.sort(
                  (a, b) =>
                    (rolelist.find(e => e.role === a.role)?.priority || 0) -
                    (rolelist.find(e => e.role === b.role)?.priority || 1)
                )[0]?.role;

          state.userRejected = role ? false : true;
          state.role = (role as Roles) || Roles.UNKNOWN;
          state.userAccessProfile = action.payload;
        }
        state.appInitialised = true;
      })
      .addCase(loadUserProfile.rejected, state => {
        state.loading = false;
        state.appInitialised = true;
        state.userRejected = true;
      });
  },
});

export const {
  setIsAuthenticated,
  clearUserData,
  setUser,
  setUserProfile,
  setAppInitialised,
  setLoading,
  setResponseErrorStatus,
  signOut,
} = sessionSlice.actions;

export default sessionSlice.reducer;
